import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import CultureLabel from "components/Culture/CultureLabel";
import Status from "components/Status/Status";
import TagList from "components/Tag/TagList";
import React from "react";
import { ContactWorksAtSection } from "./ContactWorksAtSection";
import { ContactFormState } from "contact/models";
import { contactToFormState, getInstruction } from "contact/utils";
import { PreviewPanelInPageEditableField } from "components/MainSectionTemplate/InPageEditableField";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { PreviewPanelSectionTemplate } from "components/MainSectionTemplate/PreviewPanelSectionTemplate";
import ContactEmailRow from "contact/detail/components/ContactEmailRow";
import { EmailWithBlacklist } from "contact/detail/components/EmailWithBlacklist";
import { ContactName } from "contact/detail/components/ContactName";
import { AddressInnerForm } from "components/ValidatedFields/AddressInnerForm";
import { Address } from "company/detail/components/models";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import PhoneRow from "components/DetailPage/PhoneRow";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import { isNullOrEmptyString } from "utilities";
import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import { useHasAnyTags } from "components/Tag/hooks";
import TagsSelect from "components/Tag/TagsSelect";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import { getGenderName } from "utilities/genderUtils";
import { GenderRadio } from "contact/detail/components/GenderRadio";
import UrlInput from "components/urlinput";
import TwitterLink from "components/TwitterLink";
import WebsiteLink from "components/Website/WebsiteLink";

interface Props {
  contact: server.dto.Contact;
  worksAt: server.dto.CompanyWithWorkRelation[]
  onChange: () => Promise<void>
}

export const ContactPreviewPanelContent = (props: Props) => {
  const { contact, worksAt } = props;
  const [formState, setFormState] = React.useState<ContactFormState>(contactToFormState(props.contact));
  const [tags, setTags] = React.useState<server.dto.TagSmall[]>(contact.tags);

  const hasMultipleStatuses = site.currentEnvironment.statuses.contact.length > 1;
  const hasAnyTags = useHasAnyTags("contact");

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      const [firstName, insertion, lastName] = value as string[];
      setFormState(prevState => ({
        ...prevState,
        firstName,
        insertion,
        lastName
      }));
    }
    else if (key === "position") {
      setFormState(prevState => ({
        ...prevState,
        position: value
      }));
    } else if (key === "email") {
      setFormState(prevState => ({
        ...prevState,
        emailAddresses: value[0],
        isEmailBlacklisted: value[1]
      }));
    } else if (key === "homeAddress") {
      setFormState(prevState => ({
        ...prevState,
        homeAddress: value
      }));
    } else if (key === "language") {
      setFormState(prevState => ({
        ...prevState,
        culture: value
      }));
    }
    else if (key === "mobileNumbers") {
      setFormState(prevState => ({
        ...prevState,
        mobileNumbers: value
      }));
    }
    else if (key === "landline") {
      setFormState(prevState => ({
        ...prevState,
        landlineNumbers: value
      }));
    } else if (key === "status") {
      setFormState(prevState => ({
        ...prevState,
        status: value
      }));
    } else if (key === "owner") {
      setFormState(prevState => ({
        ...prevState,
        owner: value
      }));
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "gender") {
      setFormState(prevState => ({
        ...prevState,
        gender: value
      }));
    } else if (key === "department") {
      setFormState(prevState => ({
        ...prevState,
        department: value
      }));
    } else if (key === "salutation") {
      setFormState(prevState => ({
        ...prevState,
        salutation: value
      }));
    } else if (key === "attn") {
      setFormState(prevState => ({
        ...prevState,
        attn: value
      }));
    } else if (key === "twitter") {
      setFormState(prevState => ({
        ...prevState,
        twitter: value
      }));
    }
    else if (key === "website") {
      setFormState(prevState => ({
        ...prevState,
        website: value
      }));
    } else if (key === "facebook") {
      setFormState(prevState => ({
        ...prevState,
        facebook: value
      }));
    }
    else {
      console.warn("Unhandled value change in ContactPreviewPanelContent", key, value);
    }
  }

  return <PreviewPanelSectionTemplate
    enabled={contact.permissions.canModify}
    onSuccessfulUpdate={props.onChange}
    onValueChanged={onValueChanged}
  >
    {props.contact.isDeleted && <PreviewPanelDetailRow>
      <DeletedRow />
    </PreviewPanelDetailRow>}
    {!props.contact.isValidated && <PreviewPanelDetailRow>
      <NotValidatedRow
        canApprove={props.contact.permissions.canModify}
        sublabel={RESX.Planning.InputFromWebform}
        approveEndpoint={() => window.CoreApi.Contact.Validate({
          contactId: props.contact.contactId
        })} />
    </PreviewPanelDetailRow>}

    <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Name}>
      <PreviewPanelInPageEditableField
        field={{
          key: "name",
          render: () => <div>{[formState.firstName, formState.insertion, formState.lastName].filter(Boolean).join(" ")}</div>,
          inPageEdit: {
            initialValue: [formState.firstName, formState.insertion, formState.lastName],
            render: ({ value, onChange, submit, cancel }) =>
              <ContactName
                focusOnMount
                firstName={value[0]}
                insertion={value[1]}
                lastName={value[2]}
                onChange={(firstName, insertion, lastName) => onChange([firstName, insertion, lastName])}
                onEnter={submit}
                onEsc={cancel}
                width={130}
              />,
            submitChange: ([firstName, insertion, lastName]: string[]) => {
              const instruction = getInstruction(contact, formState, {
                name: {
                  first: firstName,
                  insertion: insertion,
                  last: lastName,
                }
              });
              return window.CoreApi.Contact.UpdateDetails(instruction)
            },
            type: "confirmOrCancelButton"
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Email}>
      <PreviewPanelInPageEditableField
        field={{
          key: "email",
          isEmpty: isNullOrEmptyString(formState.emailAddresses),
          render: () => <ContactEmailRow
            emailAddresses={formState.emailAddresses}
            emailBlacklisted={formState.isEmailBlacklisted}
          />,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <EmailWithBlacklist
              value={value}
              onChange={v => onChange(v)}
              onEnter={submit}
              onEsc={cancel}
            />,
            fullWidth: true,
            submitChange: (value: [string, boolean]) => {
              const instruction = getInstruction(contact, formState, { emailAddresses: value[0], emailBlacklisted: value[1] });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            },
            type: "confirmOrCancelButton",
            initialValue: [formState.emailAddresses, formState.isEmailBlacklisted],
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Contact.Mobile}>
      <PreviewPanelInPageEditableField
        field={{
          key: "mobileNumbers",
          render: () => <PhoneRow numbers={convertToPhoneNumbers(formState.mobileNumbers)} />,
          isEmpty: isNullOrEmptyString(formState.mobileNumbers),
          inPageEdit: {
            initialValue: formState.mobileNumbers,
            render: ({ value, onChange, submit, cancel }) => <PhoneInput
              focusOnMount
              value={value}
              onChange={onChange}
              onEnter={submit}
              onEsc={cancel}
            />,
            fullWidth: true,
            type: "confirmOrCancelButton",
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { mobileNumbers: convertToPhoneNumbers(value) });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            }
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Company.Phone}>
      <PreviewPanelInPageEditableField
        field={{
          key: "landline",
          render: () => <PhoneRow numbers={convertToPhoneNumbers(formState.landlineNumbers)} />,
          isEmpty: isNullOrEmptyString(formState.landlineNumbers),
          inPageEdit: {
            initialValue: formState.landlineNumbers,
            fullWidth: true,
            render: ({ value, onChange, submit, cancel }) => <PhoneInput
              focusOnMount
              value={value}
              onChange={onChange}
              onEnter={submit}
              onEsc={cancel}
            />,
            type: "confirmOrCancelButton",
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { landlineNumbers: convertToPhoneNumbers(value) });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            }
          }
        }} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Language.resxLanguage}>
      <PreviewPanelInPageEditableField
        field={{
          key: "language",
          render: () => <CultureLabel culture={serverReference.templateCultures.find(c => c.lcid === formState.culture)} />,
          inPageEdit: {
            initialValue: formState.culture,
            fullWidth: true,
            render: ({ value, onChange }) => (
              <CulturesTemplateDropdown
                value={value}
                onChange={c => onChange(c, true)}
                config={{
                  focusOnMount: true,
                  openMenuOnFocus: true
                }}
              />
            ),
            type: "confirmOrCancelButton",
            submitChange: (value: server.dto.LCID) => {
              const instruction = getInstruction(contact, formState, { culture: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            }
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Contact.Gender}>
      <PreviewPanelInPageEditableField
        field={{
          key: "gender",
          render: () => getGenderName(formState.gender),
          inPageEdit: {
            initialValue: formState.gender,
            render: ({ value, onChange }) => (
              <GenderRadio
                value={value}
                onChange={v => onChange(v, true)}
              />
            ),
            type: "custom",
            submitChange: (value: server.dto.Genders) => {
              const instruction = getInstruction(contact, formState, { gender: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            }
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Contact.Position}>
      <PreviewPanelInPageEditableField
        field={{
          key: "position",
          render: () => formState.position,
          isEmpty: isNullOrEmptyString(formState.position),
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) =>
              <OptionalTextInput
                value={value}
                onChange={onChange}
                onEnter={submit}
                onEsc={cancel}
                maxLength={serverReference.constants.contact.maxPositionLength}
                focusOnMount
              />,
            fullWidth: true,
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { position: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            },
            type: "confirmOrCancelButton",
            initialValue: formState.position,
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Contact.Department}>
      <PreviewPanelInPageEditableField
        field={{
          key: "department",
          render: () => formState.department,
          isEmpty: isNullOrEmptyString(formState.department),
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) =>
              <OptionalTextInput
                value={value}
                onChange={onChange}
                onEnter={submit}
                onEsc={cancel}
                maxLength={serverReference.constants.contact.maxDepartmentLength}
                focusOnMount
              />,
            fullWidth: true,
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { department: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            },
            type: "confirmOrCancelButton",
            initialValue: formState.department,
          }
        }} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Tag.resxTag}>
      <PreviewPanelInPageEditableField
        field={{
          key: "tags",
          render: () => <TagList tags={tags} length="short" />,
          isEmpty: tags.length === 0,
          hidden: !hasAnyTags,
          inPageEdit: {
            render: ({ value, onChange }) => (
              <div className="main-section-template-fields-padding">
                <TagsSelect
                  mainArea="contact"
                  value={value}
                  onChange={onChange}
                />
              </div>
            ),
            type: "confirmOrCancelButton",
            initialValue: tags,
            submitChange: (tags: server.dto.TagSmall[]) => {
              return window.CoreApi.Contact.UpdateTags({ itemId: contact.contactId, tagsIds: tags.map(tag => tag.tagId) });
            }
          }
        }} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Contact.Salutation}>
      <PreviewPanelInPageEditableField
        field={{
          key: "salutation",
          render: () => formState.salutation,
          isEmpty: isNullOrEmptyString(formState.salutation),
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) =>
              <OptionalTextInput
                value={value}
                onChange={onChange}
                onEnter={submit}
                onEsc={cancel}
                maxLength={serverReference.constants.contact.maxSalutationLength}
                focusOnMount
              />,
            fullWidth: true,
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { salutation: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            },
            type: "confirmOrCancelButton",
            initialValue: formState.salutation,
          }
        }} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Contact.AttnLineOnEnvelope}>
      <PreviewPanelInPageEditableField
        field={{
          key: "attn",
          render: () => formState.attn,
          isEmpty: isNullOrEmptyString(formState.attn),
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) =>
              <OptionalTextInput
                value={value}
                onChange={onChange}
                onEnter={submit}
                onEsc={cancel}
                maxLength={serverReference.constants.contact.maxAttnLength}
                focusOnMount
              />,
            fullWidth: true,
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { attn: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            },
            type: "confirmOrCancelButton",
            initialValue: formState.attn,
          }
        }} />
    </PreviewPanelDetailRow>


    <PreviewPanelDetailRow labelTop={RESX.Contact.HomeAddress}>
      <PreviewPanelInPageEditableField
        field={{
          key: "homeAddress",
          render: () => contact.address.formatted.join(", "),
          isEmpty: !contact.address.formatted?.length,
          inPageEdit: {
            initialValue: formState.homeAddress,
            useCustomSavingState: true,
            fullWidth: true,
            render: ({ value, onChange, submit, isSaving }) => (
              <div style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">
                <AddressInnerForm
                  address={value}
                  onChange={onChange}
                  submit={submit}
                  isSaving={isSaving}
                />
              </div>
            ),
            type: "confirmOrCancelButton",
            submitChange: (value: Address) => {
              const instruction = getInstruction(contact, formState, {
                address: {
                  addressLine1: value.addressLine1,
                  addressLine2: value.addressLine2,
                  postalCode: value.postalCode,
                  city: value.city,
                  stateId: value.state.key
                }
              });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            }
          }
        }} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Contact.Twitter}>
      <PreviewPanelInPageEditableField
        field={{
          key: "twitter",
          render: () => <TwitterLink twitter={formState.twitter} />,
          isEmpty: isNullOrEmptyString(formState.twitter),
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) =>
              <OptionalTextInput
                value={value}
                onChange={onChange}
                onEnter={submit}
                onEsc={cancel}
                maxLength={serverReference.constants.contact.maxTwitterLength}
                focusOnMount
              />,
            fullWidth: true,
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { twitter: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            },
            type: "confirmOrCancelButton",
            initialValue: formState.twitter,
          }
        }} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Website}>
      <PreviewPanelInPageEditableField
        field={{
          key: "website",
          render: () => <WebsiteLink website={formState.website} />,
          isEmpty: isNullOrEmptyString(formState.website),
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) =>
              <UrlInput
                focusOnMount
                value={value}
                onValueChange={onChange}
                validatorKey="contactWebsite"
                onEnter={submit}
                onEsc={cancel}
                maxLength={serverReference.constants.contact.maxNetworkPathLength}
              />,
            fullWidth: true,
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { website: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            },
            type: "confirmOrCancelButton",
            initialValue: formState.website,
          }
        }} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={"Facebook"}>
      <PreviewPanelInPageEditableField
        field={{
          key: "facebook",
          render: () => <WebsiteLink website={formState.facebook} />,
          isEmpty: isNullOrEmptyString(formState.facebook),
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) =>
              <UrlInput
                focusOnMount
                value={value}
                onValueChange={onChange}
                validatorKey="contactFacebook"
                onEnter={submit}
                onEsc={cancel}
                maxLength={serverReference.constants.contact.maxNetworkPathLength}
              />,
            fullWidth: true,
            submitChange: (value: string) => {
              const instruction = getInstruction(contact, formState, { facebook: value });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            },
            type: "confirmOrCancelButton",
            initialValue: formState.facebook,
          }
        }} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Status.resxStatus}>
      <PreviewPanelInPageEditableField
        field={{
          key: "status",
          render: () => <Status status={formState.status} />,
          hidden: !hasMultipleStatuses,
          inPageEdit: {
            render: ({ value, onChange }) => (
              <StatusDropdown
                value={value}
                onChange={onChange}
                mainArea="contact"
                options={undefined}
                config={{
                  focusOnMount: true,
                  openMenuOnFocus: true
                }}
              />
            ),
            type: "confirmOrCancelButton",
            initialValue: formState.status,
            fullWidth: true,
            submitChange: (value: server.dto.StatusSmall) => {
              const instruction = getInstruction(contact, formState, { statusId: value.statusId });
              return window.CoreApi.Contact.UpdateDetails(instruction);
            }
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Owner}>
      <PreviewPanelInPageEditableField
        field={{
          key: "owner",
          render: () => <UserWithAvatar user={formState.owner} avatarSize="avatar-30" />,
          isEmpty: !formState.owner,
          inPageEdit: {
            render: ({ onChange }) => (
              <div className="main-section-template-fields-padding">
                <UserModalPicker
                  value={undefined}
                  onChange={v => onChange(v, true)}
                  required={false}
                  validatorKey="contactOwner"
                  canCreateNew={false}
                />
              </div>
            ),
            type: "custom",
            initialValue: formState.owner,
            submitChange: (value: server.dto.UserSmall) => {
              const instruction = getInstruction(contact, formState, {
                ownerUserId: value.userId,
              });

              return window.CoreApi.Contact.UpdateDetails(instruction);
            }
          }
        }} />
    </PreviewPanelDetailRow>

    <ContactWorksAtSection worksAt={worksAt} />
  </PreviewPanelSectionTemplate >;
}